import type { Product } from 'payments-api/model';
import {
  useGetProductsSuspense,
  usePostProducts,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import React, { Suspense, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

const TitleRow = ({ title }: { title: string }) => (
  <div className="flex flex-row items-center h-fit border-main border-b-[1px] py-f1 w-full">
    <span className="text-main">{title}</span>
  </div>
);

const ProductRow = ({
  product,
  role,
  goToProduct,
}: {
  product: Product;
  role: string;
  goToProduct: (product: Product) => void;
}) => {
  const isPaidByRole = product.paid_by_roles.some((r) => r.name === role);
  return (
    <button
      type="button"
      onClick={() => goToProduct(product)}
      aria-label={`product-details-${product.code}`}
      data-testid="product-details-button"
      className={clsx(
        'flex flex-col items-start justify-start border-main border-[1px] border-t-0 w-full p-f1 gap-y-f1 h-fit min-h-f8 hover:bg-negative'
      )}
    >
      <span className="text-main truncate w-full text-left">
        {product.code}
      </span>
      <span className="text-main-500 text-md w-full truncate text-left">
        {product.description}
      </span>
      <div className="flex flex-row items-center h-fit w-full gap-x-f1">
        <span className="text-main text-md">
          {isPaidByRole ? 'Paid By' : 'Paid To'} {role}
        </span>
      </div>
    </button>
  );
};

const NewProductRow = ({ onAdd }: { onAdd: () => void }) => (
  <button
    type="button"
    className="flex w-full h-full p-f1 bg-transparent border-main border-[1px] border-t-0 text-main-500 hover:text-main hover:bg-negative"
    onClick={onAdd}
    aria-label="Add new product"
    data-testid="add-new-product-button"
  >
    Add+
  </button>
);

const DraftProductRow = ({
  selectedRoleName,
  onSubmit,
  onCancel,
  isPending,
}: {
  selectedRoleName: string;
  onSubmit: (
    code: string,
    description: string,
    isPurchase: boolean
  ) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isPurchase, setIsPurchase] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const handleSubmit = useCallback(async () => {
    setError(undefined);
    try {
      await onSubmit(code.trim(), description.trim(), isPurchase);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    }
  }, [code, description, isPurchase, onSubmit]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-y-f1 w-full border-main border-[1px] border-t-0 p-f1 min-h-f12"
    >
      <div className="flex flex-row items-center gap-x-f1 w-full">
        <TextInput
          padding="px-f1 py-f0"
          width="w-full"
          height="h-fit"
          align="left"
          placeholder="Product code"
          size="md"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-testid="draft-product-code"
        />
      </div>
      <TextInput
        padding="px-f1 py-f0"
        width="w-full"
        height="h-fit"
        align="left"
        placeholder="Description"
        size="md"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        data-testid="draft-product-description"
      />
      <div className="flex flex-row items-center gap-x-f1 w-full">
        <Toggle
          leftLabel="Paid By"
          rightLabel="Paid To"
          onLeftClick={() => setIsPurchase(true)}
          onRightClick={() => setIsPurchase(false)}
          initialActiveButton={isPurchase ? 'left' : 'right'}
          buttonPadding="px-f1 py-f0"
          buttonHeight="h-fit"
          data-testid="draft-product-paid-to-paid-by-toggle"
        />
        <span className="text-main text-md">{selectedRoleName}</span>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
      <FormButtons
        type="secondary"
        cancelButtonLabel="Cancel"
        onCancel={onCancel}
        submitButtonLabel={isPending ? 'Creating...' : 'Create'}
        disabled={isPending || !code.trim() || !description.trim()}
        submitButtonTestId="draft-product-submit-button"
        cancelButtonTestId="draft-product-cancel-button"
      />
    </form>
  );
};

export const CatalogProductTableContent = ({
  selectedRoleName,
  onProductClick,
}: {
  selectedRoleName: string;
  onProductClick: (product: Product) => void;
}) => {
  const { data: productsData } = useGetProductsSuspense();
  const queryClient = useQueryClient();
  const createProductMutation = usePostProducts();
  const [showDraftProduct, setShowDraftProduct] = useState(false);

  const products: Product[] = productsData.data;
  const filteredProducts = products.filter(
    (product) =>
      product.paid_by_roles.some((r) => r.name === selectedRoleName) ||
      product.paid_to_roles.some((r) => r.name === selectedRoleName)
  );

  const handleDraftProductSubmit = useCallback(
    async (code: string, description: string, isPurchase: boolean) => {
      await createProductMutation.mutateAsync({
        data: {
          code,
          description,
          paid_to_roles: isPurchase ? [] : [{ name: selectedRoleName }],
          paid_by_roles: isPurchase ? [{ name: selectedRoleName }] : [],
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/products'] });
      setShowDraftProduct(false);
    },
    [createProductMutation, selectedRoleName, queryClient]
  );

  return (
    <div className="flex flex-col h-fit w-full">
      {filteredProducts.map((product) => (
        <ProductRow
          key={product.id}
          product={product}
          role={selectedRoleName}
          goToProduct={onProductClick}
        />
      ))}
      {showDraftProduct ? (
        <DraftProductRow
          selectedRoleName={selectedRoleName}
          onSubmit={handleDraftProductSubmit}
          onCancel={() => setShowDraftProduct(false)}
          isPending={createProductMutation.isPending}
        />
      ) : (
        <NewProductRow onAdd={() => setShowDraftProduct(true)} />
      )}
    </div>
  );
};

type CatalogProductTableProps = {
  selectedRoleName: string;
  onProductClick: (product: Product) => void;
};

export const CatalogProductTable = ({
  selectedRoleName,
  onProductClick,
}: CatalogProductTableProps): JSX.Element => (
  <div className="flex flex-col h-fit w-full">
    <TitleRow title="Products" />
    <Suspense
      fallback={
        <div className="flex h-f4 w-full border-main border-[1px] border-t-0 p-f1 text-main-500">
          Loading...
        </div>
      }
    >
      <CatalogProductTableContent
        selectedRoleName={selectedRoleName}
        onProductClick={onProductClick}
      />
    </Suspense>
  </div>
);
